const TARGET_RATE = 24000; // LiveAvatar LITE requires PCM16 @ 24 kHz
const CHUNK_MS = 500;      // send ~0.5 s chunks (docs allow up to 1 MB / ~1 s)

/**
 * LITE-mode audio bridge: taps the OpenAI Realtime WebRTC output stream,
 * downsamples it to PCM16 @ 24 kHz and forwards base64 chunks to the avatar
 * (`agent.speak` events published by HeyGenManager).
 *
 * Capture runs continuously once connected, but chunks are only forwarded
 * between beginSpeech()/endSpeech() — driven by the Realtime
 * `output_audio_buffer.started/stopped` events — so silence between turns is
 * never streamed to the avatar.
 */
class AvatarAudioBridge {
  constructor(sendAudio, sendEnd) {
    this._sendAudio = sendAudio; // (base64) => void
    this._sendEnd = sendEnd;     // () => void
    this._active = false;
    this._samples = [];          // accumulated Float32 samples @ TARGET_RATE
    this._sampleCount = 0;
    this._chunkSamples = Math.round(TARGET_RATE * (CHUNK_MS / 1000));
    this.context = null;
    this._source = null;
    this._processor = null;
    this._zeroGain = null;
  }

  connectStream(stream) {
    this.disconnect();
    this.context = new (window.AudioContext || window.webkitAudioContext)();
    this._source = this.context.createMediaStreamSource(stream);
    // ScriptProcessor needs a path to destination to fire; keep it silent.
    this._zeroGain = this.context.createGain();
    this._zeroGain.gain.value = 0;
    this._processor = this.context.createScriptProcessor(4096, 1, 1);
    this._processor.onaudioprocess = (e) => {
      if (!this._active) return;
      this._append(e.inputBuffer.getChannelData(0), e.inputBuffer.sampleRate);
    };
    this._source.connect(this._processor);
    this._processor.connect(this._zeroGain);
    this._zeroGain.connect(this.context.destination);
    if (this.context.state === 'suspended') this.context.resume();
  }

  /** A model response started producing audio — start forwarding. */
  beginSpeech() {
    this._samples = [];
    this._sampleCount = 0;
    this._active = true;
  }

  /** Response audio finished — flush the tail and close the speak event. */
  endSpeech() {
    if (!this._active) return;
    this._active = false;
    this._flush();
    this._sendEnd();
  }

  /** Interrupted — drop everything buffered without sending. */
  abort() {
    this._active = false;
    this._samples = [];
    this._sampleCount = 0;
  }

  _append(f32, inputRate) {
    const rate = inputRate || (this.context ? this.context.sampleRate : 48000);
    const down = rate === TARGET_RATE ? f32 : this._downsample(f32, rate);
    this._samples.push(Float32Array.from(down));
    this._sampleCount += down.length;
    if (this._sampleCount >= this._chunkSamples) this._flush();
  }

  _downsample(f32, fromRate) {
    const ratio = fromRate / TARGET_RATE;
    const outLen = Math.floor(f32.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const pos = i * ratio;
      const i0 = Math.floor(pos);
      const i1 = Math.min(i0 + 1, f32.length - 1);
      const frac = pos - i0;
      out[i] = f32[i0] * (1 - frac) + f32[i1] * frac;
    }
    return out;
  }

  _flush() {
    if (!this._sampleCount) return;
    const merged = new Float32Array(this._sampleCount);
    let offset = 0;
    for (const part of this._samples) {
      merged.set(part, offset);
      offset += part.length;
    }
    this._samples = [];
    this._sampleCount = 0;

    const pcm = new Int16Array(merged.length);
    for (let i = 0; i < merged.length; i++) {
      const s = Math.max(-1, Math.min(1, merged[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    this._sendAudio(this._toBase64(new Uint8Array(pcm.buffer)));
  }

  _toBase64(bytes) {
    let binary = '';
    const STEP = 0x8000;
    for (let i = 0; i < bytes.length; i += STEP) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + STEP));
    }
    return btoa(binary);
  }

  disconnect() {
    this._active = false;
    this._samples = [];
    this._sampleCount = 0;
    try { this._processor && (this._processor.onaudioprocess = null); } catch (_) {}
    try { this._source && this._source.disconnect(); } catch (_) {}
    try { this._processor && this._processor.disconnect(); } catch (_) {}
    try { this._zeroGain && this._zeroGain.disconnect(); } catch (_) {}
    try { this.context && this.context.close(); } catch (_) {}
    this.context = null;
    this._source = null;
    this._processor = null;
    this._zeroGain = null;
  }
}

export default AvatarAudioBridge;
